const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const MEMBER_API = isLocal ? 'http://localhost:5290' : 'https://member.api.hirethink.ai';
const CV_API = isLocal ? 'http://localhost:5293' : 'https://cv.api.hirethink.ai';
const REPORT_API = isLocal ? 'http://localhost:5293' : 'https://report.api.hirethink.ai';

const COMPANY_ID = '019d8657-6c3c-7a29-867b-8aea2d337cad';

// --- Candidate Auth Helper ---

function getCandidateToken() {
  return localStorage.getItem('candidate_token');
}

function candidateHeaders(extra = {}) {
  const token = getCandidateToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function candidateAuthFetch(url, options = {}, { skipLogout = false } = {}) {
  const token = getCandidateToken();

  // Teşhis: token yoksa Authorization başlığı hiç gönderilmez, sunucu 401 döner.
  // Böyle bir durumda 401'in kaynağı sunucu değil, oturumun kaybolmuş olmasıdır.
  if (!token) {
    console.warn('[API] candidate_token yok, Authorization başlığı gönderilmiyor:', url);
  }

  const res = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (res.status === 401 && token && !skipLogout) {
    localStorage.removeItem('candidate_token');
    localStorage.removeItem('candidate_id');
    localStorage.removeItem('candidate_profile');
    window.dispatchEvent(new Event('candidate-logout'));
  }

  return res;
}

// --- Public Endpoints (CV API, No Auth) ---

export async function searchJobList(params = {}, skip = 0, take = 20) {
  const body = {
    title: params.title || null,
    currentLocation: params.currentLocation || null,
    searchText: params.searchText || null,
    workingType: params.workingType || null,
    experienceYear: params.experienceYear ?? null,
    age: params.age ?? null,
    educationLevelId: params.educationLevelId ?? null,
    isMillitaryCompleted: params.isMillitaryCompleted ?? null,
    companyId: COMPANY_ID,
    skip,
    take,
  };

  const res = await fetch(`${CV_API}/api/Career/SearchJobList`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) throw new Error('İş arama başarısız');
  const data = await res.json();

  const items = data.data || data.items || [];
  const total = data.total || items.length;
  return { items, total };
}

// --- SearchJobs (birleşik: parse + arama) ---
// ParseSearchText + SearchJobList'in yerini alır. Tek multipart çağrı;
// bağlam `profile` alanıyla taşınır (önceki yanıttan gelen profil geri gönderilir).
//
// NOT: Bu uç şu an 401 döndürüyor, eski uçlar ise herkese açık. Ana sayfa
// araması giriş yapmamış ziyaretçiler tarafından da kullanıldığı için,
// erişilemediğinde çağıran taraf eski akışa düşebilsin diye
// err.code = 'UNAVAILABLE' işaretleniyor.
export async function searchJobs({
  searchText = null,
  cv = null,
  profile = null,
  experienceAsked = false,
  skip = 0,
  take = 20,
} = {}) {
  const fd = new FormData();
  if (cv) fd.append('cv', cv);
  if (searchText) fd.append('searchText', searchText);

  const p = profile || {};
  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== '') fd.append(key, value);
  };

  // Önceki yanıttan taşınan bağlam
  append('previousTitle', p.title);
  append('previousLocation', p.currentLocation);
  append('previousWorkingType', p.workingType);
  append('previousExperienceYear', p.experienceYear);
  if (experienceAsked) fd.append('experienceAsked', 'true');

  // Profil alanları
  append('skills', p.skills);
  append('experienceYear', p.experienceYear);
  append('englishLevel', p.englishLevel);
  append('age', p.age);
  append('isMillitaryCompleted', p.isMillitaryCompleted);
  append('educationLevelId', p.educationLevelId);

  fd.append('skip', skip);
  fd.append('take', take);

  // Giriş yapılmışsa token gönderilir; zorunlu değil
  const token = getCandidateToken();
  const res = await fetch(`${CV_API}/api/Career/SearchJobs`, {
    method: 'POST',
    body: fd,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (res.status === 401 || res.status === 403 || res.status === 404 || res.status === 501) {
    const err = new Error('SearchJobs kullanılamıyor');
    err.code = 'UNAVAILABLE';
    err.httpStatus = res.status;
    console.warn(`[SearchJobs] ${res.status}, eski arama akışına düşülüyor`);
    throw err;
  }
  if (!res.ok) throw new Error('İş arama başarısız');

  const json = await res.json();
  const data = json.data || {};
  return {
    ok: json.status === true,
    isReady: data.isReady === true,
    profile: data.profile || null,
    jobs: data.jobs || [],
    total: json.total ?? (data.jobs ? data.jobs.length : 0),
    message: json.message || null,
  };
}

export async function getJobDetailBySlug(slug) {
  const res = await fetch(`${CV_API}/api/Job/GetJobDetailBySlug?Slug=${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error('İlan detayı alınamadı');
  const json = await res.json();
  return json.data || json;
}

// Toplam yayındaki ilan sayısı ve ülke sayısı. Tarayıcı önbelleğini atlayarak
// ana sayfadaki rozet için güncel veriyi getirir.
export async function getJobCount() {
  const res = await fetch(`${CV_API}/api/Isbulsun/GetJobCount`, { cache: 'no-store' });
  if (!res.ok) throw new Error('İlan sayısı alınamadı');
  const json = await res.json();
  if (json.status === false) throw new Error(json.message || 'İlan sayısı alınamadı');
  return json.data || {};
}

// --- ParseSearchText (CV API, Public) ---

let parseContext = {
  previousTitle: null,
  previousLocation: null,
  previousWorkingType: null,
  previousExperienceYear: null,
  experienceAsked: false,
};

export function resetParseContext() {
  parseContext = {
    previousTitle: null,
    previousLocation: null,
    previousWorkingType: null,
    previousExperienceYear: null,
    experienceAsked: false,
  };
}

export async function parseSearchText(text, cvFile = null) {
  const formData = new FormData();
  formData.append('searchText', text);
  if (cvFile) {
    formData.append('cv', cvFile);
  }

  if (parseContext.previousTitle) formData.append('previousTitle', parseContext.previousTitle);
  if (parseContext.previousLocation) formData.append('previousLocation', parseContext.previousLocation);
  if (parseContext.previousWorkingType) formData.append('previousWorkingType', parseContext.previousWorkingType);
  if (parseContext.previousExperienceYear != null) formData.append('previousExperienceYear', parseContext.previousExperienceYear);
  if (parseContext.experienceAsked) formData.append('experienceAsked', 'true');

  const res = await fetch(`${CV_API}/api/Career/ParseSearchText`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) throw new Error('Metin analiz edilemedi');
  const data = await res.json();

  const d = data.data || data;
  if (d.title) parseContext.previousTitle = d.title;
  if (d.currentLocation) parseContext.previousLocation = d.currentLocation;
  if (d.workingType) parseContext.previousWorkingType = d.workingType;
  if (d.experienceYear != null) parseContext.previousExperienceYear = d.experienceYear;

  if (data.clarifyQuestion && /tecrübe|deneyim|experience|yıl/i.test(data.clarifyQuestion)) {
    parseContext.experienceAsked = true;
  }

  return data;
}

// --- CV Autofill (CV API, Public) ---

export async function cvAutofill(file) {
  const formData = new FormData();
  formData.append('Cv', file);

  const res = await fetch(`${CV_API}/api/Job/CvAutofill`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) throw new Error('CV analiz edilemedi');
  const json = await res.json();
  const data = json.data || json;

  // Debug: API response'unu logla
  console.log('[CvAutofill] API raw response:', JSON.stringify(data, null, 2));

  return {
    name: data.name || null,
    surname: data.surname || null,
    fullName: data.fullName || data.name ? [data.name, data.surname].filter(Boolean).join(' ') : null,
    email: data.email || null,
    phone: data.phone || null,
    title: data.title || data.position || data.jobTitle || null,
    currentLocation: data.location || data.currentLocation || data.city || null,
    experienceYear: data.experienceYear ?? data.experience ?? null,
    skills: data.skills || null,
    summary: data.summary || data.about || data.profileSummary || null,
    education: data.education || data.educationLevel || null,
    educationLevelId: data.educationLevelId ?? null,
    birthdate: data.birthdate || data.dateOfBirth || data.birthDate || null,
    militaryStatus: data.militaryStatus || null,
    hobbies: data.hobbies || data.sportsHobbies || null,
    age: data.age ?? null,
    // Deneyimler, API farklı formatlarda dönebilir
    experiences: data.experiences || data.workExperience || data.workHistory || data.jobs || null,
    // Eğitimler
    educations: data.educations || data.educationHistory || data.schools || null,
    // Sertifikalar
    certificates: data.certificates || data.certifications || null,
    // Diller
    languages: data.languages || null,
    // Ham veri
    raw: data,
  };
}

// --- CheckFitScoreByCv (kayıtlı CV + ilan linki) ---
// Manuel dosya yükleme yerine adayın sistemde kayıtlı CV'sini kullanır.
// jobId → isBulsun ilanı (linkten slug çözülerek bulunur)
// jobUrl → harici ilan linki (BE tarafında okunması gerekir)
export async function checkFitScoreByCv({ cvId, jobId = null, jobUrl = null }) {
  const res = await candidateAuthFetch(`${CV_API}/api/Career/CheckFitScoreByCv`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cvId, jobId, jobUrl }),
  }, { skipLogout: true });

  // Endpoint henüz yayında değilse çağıran taraf ayırt edebilsin
  if (res.status === 404 || res.status === 501) {
    const err = new Error('Uyum analizi servisi henüz hazır değil');
    err.code = 'NOT_IMPLEMENTED';
    throw err;
  }
  if (!res.ok) throw new Error('Uyum analizi yapılamadı');

  const json = await res.json();
  if (json.status === false) throw new Error(json.message || 'Uyum analizi yapılamadı');
  return json.data || json;
}

// Ortak hata ayrımı.
// DİKKAT: Hem Members hem CV API kimliksiz VE var olmayan rotalara da 401
// döndürüyor. Bu yüzden 401 tek başına "oturum bitti" anlamına gelmez,
// yazma uçlarında otomatik logout tetiklenmemeli.
async function assertApiOk(res, fallbackMessage) {
  if (res.ok) return;

  let body = '';
  try { body = await res.clone().text(); } catch { /* yok say */ }
  console.error(`[API] ${res.url} → ${res.status}`, body.slice(0, 300));

  const err = new Error(fallbackMessage);
  if (res.status === 404 || res.status === 501) err.code = 'NOT_IMPLEMENTED';
  else if (res.status === 401 || res.status === 403) err.code = 'UNAUTHORIZED';
  err.httpStatus = res.status;
  throw err;
}

// --- Abonelik paketleri (Members API, public) ---
export async function getSubscriptionPlanList({ targetType, category = 'AutoApply' } = {}) {
  const body = { category };
  if (targetType) body.targetType = targetType;

  const res = await fetch(`${MEMBER_API}/api/SubscriptionPlan/GetSubscriptionPlanList`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Paketler alınamadı');
  const json = await res.json();
  if (json.status === false) throw new Error(json.message || 'Paketler alınamadı');
  return json.data || [];
}

// --- Abonelik / Ödeme (iyzico) ---
// BE'DE HENÜZ YOK. Kart bilgisi hiçbir zaman frontend'e uğramaz: backend
// iyzico'nun abonelik checkout form'unu başlatır, iyzico'nun döndürdüğü
// içerik kullanıcıya gösterilir.
//
// Beklenen yanıt (ikisinden biri):
//   { checkoutFormContent: "<script>...</script>" }  → modal içinde gömülü form
//   { paymentPageUrl: "https://..." }                → iyzico sayfasına yönlendirme
//
// planId → src/data/plans.js içindeki id. iyzico'daki pricingPlanReferenceCode
// eşlemesi backend'de tutulur (iyzico panelinde tanımlanan plan kodları).
// BE mesajı → çağıran tarafın ayırt edebileceği kod
const SUBSCRIPTION_ERRORS = [
  [/phone-required/i, 'PHONE_REQUIRED'],
  [/email-required/i, 'EMAIL_REQUIRED'],
  [/already-subscribed/i, 'ALREADY_SUBSCRIBED'],
  [/plan-not-found/i, 'PLAN_NOT_FOUND'],
  [/not-configured|not_configured/i, 'NOT_IMPLEMENTED'],
];

// Telefonu API'nin beklediği biçime getir: +905xxxxxxxxx
export function normalizePhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('90')) return `+${digits}`;
  if (digits.startsWith('0')) return `+90${digits.slice(1)}`;
  if (digits.startsWith('5')) return `+90${digits}`;
  return `+${digits}`;
}

// Ödeme öncesi zorunlu profil alanları. iyzico telefon ve ad/soyad olmadan
// checkout açmıyor; eksikse önce profil güncellenmeli.
// Yanıt: { hasPhone, hasName, hasSurname }
export async function checkPaymentInfo() {
  const res = await candidateAuthFetch(`${MEMBER_API}/api/Payment/CheckPaymentInfo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  }, { skipLogout: true });

  await assertApiOk(res, 'Ödeme bilgileri kontrol edilemedi');
  const json = await res.json();
  if (json.status === false) throw new Error(json.message || 'Ödeme bilgileri kontrol edilemedi');
  return json.data || {};
}

// candidateId gönderilmez, BE JWT'den alır
export async function startSubscription({ planId } = {}) {
  const res = await candidateAuthFetch(`${MEMBER_API}/api/Payment/StartSubscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ planId }),
  }, { skipLogout: true });

  await assertApiOk(res, 'Ödeme başlatılamadı');

  const json = await res.json();
  if (json.status === false) {
    const message = json.message || '';
    console.warn('[Payment] StartSubscription status:false,', message || '(mesaj yok)');
    const err = new Error(message || 'Ödeme başlatılamadı');
    const match = SUBSCRIPTION_ERRORS.find(([re]) => re.test(message));
    if (match) err.code = match[1];
    throw err;
  }

  if (!json.data?.checkoutFormContent && !json.data?.paymentPageUrl) {
    console.warn('[Payment] Yanıtta checkoutFormContent/paymentPageUrl yok:', json.data);
  }
  return json.data || json;
}

// Mevcut abonelik bilgisi
// Beklenen dönüş: { planId, status, startDate, nextPaymentDate,
//                   subscriptionReferenceCode, cancelledAt }
// status: ACTIVE | PENDING | CANCELED | EXPIRED | UNPAID
// candidateId JWT'den alınır; imza geriye dönük uyum için duruyor
export async function getSubscription() {
  const res = await candidateAuthFetch(`${MEMBER_API}/api/Payment/GetSubscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  }, { skipLogout: true });

  await assertApiOk(res, 'Abonelik bilgisi alınamadı');

  const json = await res.json();
  if (json.status === false) {
    // "abonelik yok" veya "hâlâ işleniyor", hata değil, boş durum.
    // BE canlıda İngilizce "subscription-not-found" yerine Türkçe
    // "Aktif aboneliğiniz bulunmuyor." mesajı dönüyor, ikisi de eşleşmeli,
    // aksi halde abonelik olmayan her aday "Abonelik bilgileri alınamadı"
    // hatası görür (candidateId 01a0cd46-ada9-70a3-99a4-f682906b545b ile
    // canlıda doğrulandı).
    if (/subscription-not-found|aktif abonelik(iniz)? bulunmuyor/i.test(json.message || '')) return null;
    throw new Error(json.message || 'Abonelik bilgisi alınamadı');
  }
  return json.data || null;
}

// Abonelik iptali, dönem sonuna kadar hizmet devam eder
// Adayın tüm abonelikleri. GetSubscription şu an tek (Active) abonelik
// döndürüyor; BE dizi döndürmeye başlarsa burası kendiliğinden çoklu çalışır.
export async function getSubscriptions(candidateId) {
  const data = await getSubscription(candidateId);
  if (!data) return [];
  return Array.isArray(data) ? data.filter(Boolean) : [data];
}

export async function cancelSubscription() {
  const res = await candidateAuthFetch(`${MEMBER_API}/api/Payment/CancelSubscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  }, { skipLogout: true });

  await assertApiOk(res, 'Abonelik iptal edilemedi');

  const json = await res.json();
  if (json.status === false) throw new Error(json.message || 'Abonelik iptal edilemedi');
  return json.data || json;
}

// --- Candidate Profile (CV API, Auth Required) ---

// Aday profili, GetCandidateDetail'in yerini aldı.
// Tek DB sorgusu, çok daha hızlı. CandidateId JWT'den alınır, gövde boş gider.
// Yanıt: { status, data: { ...aday alanları, isAutoApplyEnabled } }
export async function getCandidateProfile() {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/GetCandidateProfile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  }, { skipLogout: true });

  await assertApiOk(res, 'Profil bilgileri alınamadı');

  const json = await res.json();
  if (json.status === false) throw new Error(json.message || 'Profil bilgileri alınamadı');
  return json.data || null;
}

// Hesabı pasife çekme (soft delete), BE'DE HENÜZ YOK.
// Kayıt fiziksel olarak silinmez; isActive=false yapılıp kişisel veriler
// anonimleştirilir, aktif abonelik iptal edilir.
export async function deactivateAccount({ reason = null } = {}) {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/DeactivateAccount`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  }, { skipLogout: true });

  await assertApiOk(res, 'Hesap silinemedi');

  const json = await res.json();
  if (json.status === false) throw new Error(json.message || 'Hesap silinemedi');
  return json.data ?? true;
}

export async function createCandidate(data) {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/CreateCandidate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }, { skipLogout: true });
  await assertApiOk(res, 'Profil kaydedilemedi');
  return res.json();
}

// NOT: id gönderilmez, BE JWT'den alır. Gönderilse bile ayıklanıyor ki
// eski çağrı yerleri yanlışlıkla id sızdırmasın.
export async function updateCandidate(data) {
  const { id, candidateId, ...payload } = data || {};
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/UpdateCandidate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }, { skipLogout: true });
  await assertApiOk(res, 'Profil güncellenemedi');
  return res.json();
}

export async function uploadProfilePhoto(candidateId, file) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('candidateId', candidateId);
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/UploadProfilePhoto`, {
    method: 'POST',
    body: formData,
  }, { skipLogout: true });
  await assertApiOk(res, 'Fotoğraf yüklenemedi');
  return res.json();
}

// --- CV CRUD (CV API, Auth Required) ---

// --- CV Soruları (BE'den) ---
// scope: 'Domestic' | 'abroad' (boş/'cv' → yalnızca temel CV soruları)
// scope='abroad' ile dönen soruların tamamı Yurt Dışı Planı akışında gösterilir.
// Yanıt alanları: { id, scope, groupKey, section, key, fieldKey, title,
//                   placeholder, inputType, options, isRepeatable, isRequired, sortOrder,
//                   dependsOnKey, dependsOnValue, isFollowUp, parentKey, isAiFollowUpEnabled }
export async function getCvQuestions(scope = 'Domestic') {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/GetCvQuestions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scope }),
  }, { skipLogout: true });

  if (!res.ok) {
    const err = new Error('CV soruları alınamadı');
    err.code = 'UNAVAILABLE';
    err.httpStatus = res.status;
    console.warn(`[CV] GetCvQuestions ${res.status}, yerel sorulara düşülüyor`);
    throw err;
  }

  const json = await res.json();
  if (json.status === false) throw new Error(json.message || 'CV soruları alınamadı');
  return json.data || [];
}

// BE sorusunu sayfanın beklediği şekle çevirir
export function normalizeCvQuestion(raw) {
  const inputType = String(raw.inputType || 'text').toLowerCase();
  const type =
    inputType === 'checkbox' ? 'checkbox'
      : (inputType === 'select' || inputType === 'radio') ? 'choice'
        : inputType === 'textarea' ? 'textarea'
          : inputType; // text | email | tel | month

  // options iki biçimde gelebiliyor: JSON dizi metni ya da virgülle ayrılmış liste
  let options = [];
  if (raw.options) {
    const text = String(raw.options).trim();
    if (text.startsWith('[')) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) options = parsed;
      } catch {
        console.warn('[CV] options ayrıştırılamadı:', raw.key, text);
      }
    } else {
      options = text.split(',').map((o) => o.trim()).filter(Boolean);
    }
  }

  // dependsOnValue virgülle ayrılmış olabilir (eski abroad_question anketi)
  const dependsOnValues = raw.dependsOnValue
    ? String(raw.dependsOnValue).split(',').map((v) => v.trim()).filter(Boolean)
    : [];

  return {
    id: raw.id,
    scope: raw.scope || null,
    key: raw.key,
    fieldKey: raw.fieldKey,
    groupKey: raw.groupKey || null,
    section: raw.section || null,
    text: raw.title,
    placeholder: raw.placeholder || '',
    type,
    choices: options.map((o) => ({ label: String(o), value: String(o) })),
    repeatable: raw.isRepeatable === true,
    required: raw.isRequired === true,
    sortOrder: raw.sortOrder ?? 0,
    // Koşullu gösterim + AI ek soru (eski abroad_question anketinden)
    dependsOnKey: raw.dependsOnKey || null,
    dependsOnValues,
    aiFollowUp: raw.isAiFollowUpEnabled === true,
    isFollowUp: raw.isFollowUp === true,
    parentKey: raw.parentKey || null,
  };
}

// "yok", "geç", "-" gibi cevaplar CV'ye yazılmamalı
const SKIP_ANSWERS = [
  'yok', 'yoktur', 'gec', 'geç', 'gecmek', 'geçmek', 'atla', 'hayir', 'hayır',
  'bilmiyorum', 'bos', 'boş', 'none', 'no', 'skip', 'n/a', 'na', '-', '--', '.',
];

export function isSkipAnswer(value) {
  if (value === null || value === undefined) return true;
  const v = String(value).trim().toLowerCase();
  if (!v) return true;
  // Noktalama temizlenmiş hâliyle karşılaştır ("yok." → "yok")
  const clean = v.replace(/[.!,;:\s]+$/g, '');
  return SKIP_ANSWERS.includes(clean);
}

// Anlamlı değilse boş döndürür, payload'a "yok" yazılmasın
export function cleanAnswer(value) {
  return isSkipAnswer(value) ? '' : String(value).trim();
}

// fieldKey → payload yolu. Alan adları hard-code edilmez:
//   cv.summary          → { summary }
//   experience.company  → experiences[i].company
//   education.school    → educations[i].school
export function applyFieldKey(payload, fieldKey, value, index = 0) {
  if (!fieldKey) return payload;
  const clean = cleanAnswer(value);
  if (!clean) return payload;

  const dot = fieldKey.indexOf('.');
  const prefix = dot === -1 ? 'cv' : fieldKey.slice(0, dot);
  const field = dot === -1 ? fieldKey : fieldKey.slice(dot + 1);

  if (prefix === 'experience' || prefix === 'education') {
    const listKey = prefix === 'experience' ? 'experiences' : 'educations';
    if (!Array.isArray(payload[listKey])) payload[listKey] = [];
    while (payload[listKey].length <= index) payload[listKey].push({});
    payload[listKey][index][field] = clean;
    return payload;
  }

  payload[field] = clean;
  return payload;
}

export async function createCv(data) {
  // candidateId gönderilmez, BE JWT'den çözer
  const { candidateId, ...payload } = data || {};
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/CreateCv`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }, { skipLogout: true });
  if (!res.ok) throw new Error('CV kaydedilemedi');
  return res.json();
}

export async function getCvList() {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/GetCvList`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  }, { skipLogout: true });
  if (!res.ok) throw new Error('CV listesi alınamadı');
  return res.json();
}

export async function getCvDetail(cvId) {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/GetCvDetail`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: cvId }),
  });
  if (!res.ok) throw new Error('CV detayı alınamadı');
  return res.json();
}

export async function deleteCv(cvId) {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/DeleteCv`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: cvId }),
  });
  if (!res.ok) throw new Error('CV silinemedi');
  return res.json();
}

// --- Candidate Answers (CV API, Auth Required) ---
// Sorular artık GetCvQuestions({ scope: 'abroad' }) üzerinden geliyor (bkz. yukarı);
// GetAbroadQuestions endpoint'i kaldırıldı.

// Soru, bağlı olduğu cevaba göre gösterilmeli mi?
export function isAbroadQuestionVisible(question, answers) {
  if (!question?.dependsOnKey) return true;
  const parentAnswer = answers?.[question.dependsOnKey];
  if (!parentAnswer) return false;
  if (!question.dependsOnValues?.length) return true;
  return question.dependsOnValues.some(
    (v) => String(v).toLowerCase() === String(parentAnswer).toLowerCase()
  );
}

// AI ek soru analizi. isSufficient=false ise followUpQuestion sorulur.
export async function analyzeCandidateAnswer({ key, value }) {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/AnalyzeCandidateAnswer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, value }),
  }, { skipLogout: true });

  if (!res.ok) throw new Error('Cevap analiz edilemedi');
  const json = await res.json();
  if (json.status === false) throw new Error(json.message || 'Cevap analiz edilemedi');
  return json.data || {};
}

// items:
//   ana cevap    → { key, value }
//   ek soru cevabı → { parentKey, followUpQuestion, value }   (key GÖNDERİLMEZ)
// candidateId gönderilmez, BE JWT'den çözer
export async function saveCandidateAnswers(items) {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/SaveCandidateAnswers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  }, { skipLogout: true });
  if (!res.ok) throw new Error('Yurt dışı bilgileri kaydedilemedi');
  return res.json();
}

export async function getCandidateAnswers(candidateId) {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/GetCandidateAnswers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ candidateId }),
  }, { skipLogout: true });
  if (!res.ok) throw new Error('Yurt dışı bilgileri alınamadı');
  return res.json();
}

// --- CandidateLogin API (Members API, Public) ---

export async function googleConnect(code, redirectUri) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/GoogleConnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: code, redirectUri }),
  });
  if (!res.ok) throw new Error('Google ile giriş başarısız');
  return res.json();
}

export async function facebookConnect(accessToken) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/FacebookConnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: accessToken }),
  });
  if (!res.ok) throw new Error('Facebook ile giriş başarısız');
  return res.json();
}

export async function linkedInConnect(code, redirectUri) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/LinkedInConnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: code, redirectUri }),
  });
  if (!res.ok) throw new Error('LinkedIn ile giriş başarısız');
  return res.json();
}

export async function sendVerificationCode(email) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/SendVerificationCode`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Doğrulama kodu gönderilemedi');
  return res.json();
}

export async function verifyCode(email, code) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/VerifyCode`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: String(email || '').trim(),
      token: String(code || '').trim(),
      source: 'isbulsun.com',
    }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new Error(json?.message || 'Kod doğrulanamadı');
  return json;
}

export async function sendMagicLink(email) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/SendMagicLink`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Magic link gönderilemedi');
  return res.json();
}

export async function verifyMagicLink(token) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/VerifyMagicLink`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new Error('Magic link doğrulanamadı');
  return res.json();
}

// --- Contact Form (CV API) ---

export async function sendContactForm({ name, email, phone, message }) {
  const res = await fetch(`${REPORT_API}/api/Report/SendContactForm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, phone, message }),
  });
  if (!res.ok) throw new Error('Mesaj gönderilemedi');
  return res.json();
}

// --- Job Apply (CV API) ---

export async function applyJob(fields) {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });

  const res = await candidateAuthFetch(`${CV_API}/api/Job/ApplyJob`, {
    method: 'POST',
    body: formData,
  }, { skipLogout: true });

  await assertApiOk(res, 'Başvuru gönderilemedi');
  const json = await res.json();
  // Diğer tüm uçlarda olduğu gibi HTTP 200 + status:false gerçek hata
  // olabilir, bunu kontrol etmeden `setApplied(true)` çağrılırsa
  // başarısız başvuru bile "iletildi" gösterilebilir.
  if (json.status === false) throw new Error(json.message || 'Başvuru gönderilemedi');
  return json;
}
